import * as E from 'fp-ts/lib/Either';
import Immutable from 'immutable';
import * as t from 'io-ts';
import _, {omit} from 'lodash';

import {AsyncStatusesEnum} from '../../libs/shared/helpers/src/types/asyncStatuses';
import {UnreachableError} from '../../libs/shared/helpers/src/types/customErrors';
import {AppParametricSelector, AppSelector} from '../helpers/reselectHelpers';
import {makeEnumRuntimeType} from '../models/api/apiTypes';

/*
 * Runtime types.
 */

export enum AsyncMoreStatusesEnum {
  NONE = 'NONE',
  HAS_MORE = 'HAS_MORE',
  LOADING = 'LOADING',
  ALL_LOADED = 'ALL_LOADED',
  ERROR = 'ERROR',
}
export const RAsyncMoreStatus = makeEnumRuntimeType(AsyncMoreStatusesEnum);

/*
 * Shared types.
 */

export const RAsyncStatus = makeEnumRuntimeType(AsyncStatusesEnum);

/*
 * Types.
 */

export interface ResourceMap<TResource> {
  readonly [resourceId: number]: TResource | undefined;
  readonly [resourceId: string]: TResource | undefined;
}

export interface WritableResourceMap<TResource> {
  [resourceId: number]: TResource | undefined;
  [resourceId: string]: TResource | undefined;
}

// Based on https://redux.js.org/recipes/structuring-reducers/normalizing-state-shape/
export interface ResourceTable<TId extends number | string, TResource> {
  byId: ResourceMap<TResource>;
  deletedIds: Immutable.Set<TId>;
}

export enum ResourceTableStatusesEnum {
  INITIAL,
  LOADING,
  LOADED,
  FAILED,
}

export const RResourceTableStatus = makeEnumRuntimeType(ResourceTableStatusesEnum);

export interface AsyncStatus {
  status: ResourceTableStatusesEnum;
  isStale: boolean;
  error?: string;
}

export type LazyResourceTable<TId extends number | string, TResource> = ResourceTable<TId, TResource> &
  AsyncStatus;

export enum ResourceMapAffinitiesEnum {
  NEUTRAL = 'NEUTRAL',
  PERMANENT = 'PERMANENT',
  TRANSIENT = 'TRANSIENT',
}

export type GetShouldPersist<TResource> = AppParametricSelector<{resource: TResource}, boolean>;

export class ResourceMapType<TIdType extends t.Mixed, TResourceType extends t.Mixed> extends t.ReadonlyType<
  t.Mixed,
  ResourceMap<t.TypeOf<TResourceType>>
> {
  constructor(
    idType: TIdType,
    resourceType: TResourceType,
    getAffinity: AppSelector<ResourceMapAffinitiesEnum>,
    getShouldPersist?: GetShouldPersist<t.TypeOf<TResourceType>>,
  ) {
    const baseType = t.readonly(t.record(idType, t.union([t.undefined, resourceType])));
    super('resourceMap', baseType.is, baseType.validate, baseType.encode, baseType.type);

    this.getAffinity = getAffinity;
    this.getShouldPersist = getShouldPersist;
  }

  readonly getAffinity: AppSelector<ResourceMapAffinitiesEnum>;
  readonly getShouldPersist?: GetShouldPersist<t.TypeOf<TResourceType>>;
}

/*
 * Factories.
 */

export function makeResourceMap<TIdType extends t.Mixed, TResourceType extends t.Mixed>(
  idType: TIdType,
  resourceType: TResourceType,
) {
  return new ResourceMapType(idType, resourceType, () => ResourceMapAffinitiesEnum.NEUTRAL);
}

export function makeDynamicResourceMap<TIdType extends t.Mixed, TResourceType extends t.Mixed>(
  idType: TIdType,
  resourceType: TResourceType,
  getAffinity: AppSelector<ResourceMapAffinitiesEnum>,
) {
  return new ResourceMapType(idType, resourceType, getAffinity);
}

export function makePermanentResourceMap<TIdType extends t.Mixed, TResourceType extends t.Mixed>(
  idType: TIdType,
  resourceType: TResourceType,
) {
  return new ResourceMapType(idType, resourceType, () => ResourceMapAffinitiesEnum.PERMANENT);
}

export function makeTransientResourceMap<TIdType extends t.Mixed, TResourceType extends t.Mixed>(
  idType: TIdType,
  resourceType: TResourceType,
  getShouldPersist?: GetShouldPersist<t.TypeOf<TResourceType>>,
) {
  return new ResourceMapType(
    idType,
    resourceType,
    () => ResourceMapAffinitiesEnum.TRANSIENT,
    getShouldPersist,
  );
}

export function makeTransientResourceTable<TIdType extends t.Mixed, TResourceType extends t.Mixed>(
  idType: TIdType,
  resourceType: TResourceType,
  getShouldPersist?: GetShouldPersist<t.TypeOf<TResourceType>>,
) {
  const primitiveType = t.readonlyArray(idType);

  return t.type({
    byId: makeTransientResourceMap(idType, resourceType, getShouldPersist),
    deletedIds: new t.Type(
      `${idType.name}ImmutableSet`,
      (u): u is Immutable.Set<t.TypeOf<TIdType>> => Immutable.Set.isSet(u) && u.every(idType.is),
      (i, context) =>
        E.either.chain(primitiveType.validate(i, context), (array) => t.success(Immutable.Set(array))),
      (a) => primitiveType.encode(a.toArray()),
    ),
  });
}

export function makePermanentResourceTable<TIdType extends t.Mixed, TResourceType extends t.Mixed>(
  idType: TIdType,
  resourceType: TResourceType,
) {
  const primitiveType = t.readonlyArray(idType);

  return t.type({
    byId: makePermanentResourceMap(idType, resourceType),
    deletedIds: new t.Type(
      `${idType.name}ImmutableSet`,
      (u): u is Immutable.Set<t.TypeOf<TIdType>> => Immutable.Set.isSet(u) && u.every(idType.is),
      (i, context) =>
        E.either.chain(primitiveType.validate(i, context), (array) => t.success(Immutable.Set(array))),
      (a) => primitiveType.encode(a.toArray()),
    ),
  });
}

export function makePermanentLazyResourceTable<TIdType extends t.Mixed, TResourceType extends t.Mixed>(
  idType: TIdType,
  resourceType: TResourceType,
) {
  return t.intersection([
    makePermanentResourceTable(idType, resourceType),
    t.type({
      status: RResourceTableStatus,
      isStale: t.boolean,
    }),
    t.partial({
      error: t.string,
    }),
  ]);
}

/*
 * Helpers.
 */

export const emptyResourceTable: ResourceTable<any, any> = Object.freeze({
  byId: {},
  deletedIds: Immutable.Set(),
});

export const emptyLazyResourceTable: LazyResourceTable<any, any> = Object.freeze({
  ...emptyResourceTable,
  status: ResourceTableStatusesEnum.INITIAL,
  isStale: false,
});

type ResourceWithId = {id: number | string} | {uid: number | string};

type IdOfResource<TResource extends ResourceWithId> = TResource extends {id: infer TId}
  ? TId
  : TResource extends {uid: infer TId}
  ? TId
  : never;

// If the resource has a `id` or `uid` property, infer the ID type automatically.
export function createResourceTable<TResource extends ResourceWithId>(
  resources: ResourceMap<TResource>,
  deletedIds?: ReadonlyArray<IdOfResource<TResource>>,
): ResourceTable<IdOfResource<TResource>, TResource>;
// Otherwise, expose an ID type parameter.
export function createResourceTable<TId extends number | string, TResource>(
  resources: ResourceMap<TResource>,
  deletedIds?: ReadonlyArray<TId>,
): ResourceTable<TId, TResource>;
export function createResourceTable<TId extends number | string, TResource>(
  resources: ResourceMap<TResource>,
  deletedIds?: ReadonlyArray<TId>,
): ResourceTable<TId, TResource> {
  return {
    ...emptyResourceTable,
    byId: resources,
    deletedIds: deletedIds ? emptyResourceTable.deletedIds.concat(deletedIds) : emptyResourceTable.deletedIds,
  };
}

// If the resource has a `id` or `uid` property, infer the ID type automatically.
export function createLazyResourceTable<TResource extends ResourceWithId>(
  resources: ResourceMap<TResource>,
  deletedIds?: ReadonlyArray<IdOfResource<TResource>>,
  status?: ResourceTableStatusesEnum,
): LazyResourceTable<IdOfResource<TResource>, TResource>;
// Otherwise, expose an ID type parameter.
export function createLazyResourceTable<TId extends number | string, TResource>(
  resources: ResourceMap<TResource>,
  deletedIds?: ReadonlyArray<TId>,
  status?: ResourceTableStatusesEnum,
): LazyResourceTable<TId, TResource>;
export function createLazyResourceTable<TId extends number | string, TResource>(
  resources: ResourceMap<TResource>,
  deletedIds?: ReadonlyArray<TId>,
  status: ResourceTableStatusesEnum = ResourceTableStatusesEnum.LOADED,
): LazyResourceTable<TId, TResource> {
  return {
    ...emptyLazyResourceTable,
    ...createResourceTable(resources, deletedIds),
    status,
  };
}

export function mergeResourceTables<
  TId extends number | string,
  TResource,
  TResourceTable extends ResourceTable<TId, TResource> = ResourceTable<TId, TResource>,
>(left: TResourceTable, right: TResourceTable): TResourceTable {
  return {
    ...left,
    ...right,
    byId: {
      ...omit(left.byId, ...right.deletedIds),
      ...omit(right.byId, ...left.deletedIds),
    },
    deletedIds: left.deletedIds.merge(right.deletedIds),
  };
}

export function deleteFromResourceTable<
  TId extends number | string,
  TResource,
  TResourceTable extends ResourceTable<TId, TResource> = ResourceTable<TId, TResource>,
>(
  resources: TResourceTable,
  idsOrSingleId: TId | ReadonlyArray<TId>,
  shouldTombstone = true,
): TResourceTable {
  const ids = Array.isArray(idsOrSingleId) ? idsOrSingleId : [idsOrSingleId];
  const newDeletedIds = resources.deletedIds.union(ids);
  return {
    ...resources,
    byId: omit(resources.byId, ...ids),
    deletedIds: shouldTombstone ? newDeletedIds : resources.deletedIds,
  };
}

// Translate an array to a resource map, using the idPredicate function to determine the id of the resource.
export function toResourceMap<T>(
  array: ReadonlyArray<T>,
  idPredicate: (item: T) => number | string,
): ResourceMap<T> {
  return _.keyBy(array, idPredicate);
}

// Translate an array to a resource map, keying by id.
export function toResourceMapById<T extends {id: number | string}>(array: ReadonlyArray<T>): ResourceMap<T> {
  return toResourceMap(array, (item) => item.id);
}

export function asyncMoreStatusToAsyncStatus(asyncMoreStatus: AsyncMoreStatusesEnum): AsyncStatusesEnum {
  switch (asyncMoreStatus) {
    case AsyncMoreStatusesEnum.ALL_LOADED:
    case AsyncMoreStatusesEnum.HAS_MORE:
    case AsyncMoreStatusesEnum.NONE:
      return AsyncStatusesEnum.LOADED;

    case AsyncMoreStatusesEnum.LOADING:
      return AsyncStatusesEnum.LOADING;

    case AsyncMoreStatusesEnum.ERROR:
      return AsyncStatusesEnum.FAILED;

    default:
      throw new UnreachableError(asyncMoreStatus);
  }
}
